import { PORTAL_PAGES } from '../portal/navigation.mjs'
import { CONTENT_PATHS } from './site-content.mjs'

// 실제 라우트 템플릿만 수집한다. 동적 slug·query·fragment·인증 복구 토큰은 전송하지 않는다.
export const VISIT_PAGES = [
  ...['/', '/login', '/password-reset', '/cyber/:slug', '/denied', '/unavailable'].map(path => ({ path, protectedPage: false, manager: false })),
  ...PORTAL_PAGES.map(page => ({ path: page.path, protectedPage: Boolean(page.protected), manager: false })),
  ...['/account', '/account/security', '/account/profile', '/manager/account', '/manager/security', '/manager/profile', '/manager/companies', '/manager/companies/:slug', '/manager/contracts', '/manager/contracts/:slug'].map(path => ({ path, protectedPage: true, manager: path.startsWith('/manager/') })),
  ...['/manager/enrollments', '/manager/enrollments/:slug/list', '/manager/enrollments/:slug/register', '/manager/enrollments/:slug/export'].map(path => ({ path, protectedPage: true, manager: true })),
  ...['/manager/applications', '/manager/applications/:slug/list', '/manager/applications/:slug/create'].map(path => ({ path, protectedPage: true, manager: true })),
  ...['/manager/bulk', '/manager/bulk/:slug/list', '/manager/bulk/:slug/create'].map(path => ({ path, protectedPage: true, manager: true })),
  ...['/course/detail/:contentKey', '/board/:boardId', '/board/:boardId/posts/:postId'].map(path => ({ path, protectedPage: false, manager: false })),
  ...['/my/learning/:id', '/my/assessments/:enroll/:assignment', '/my/tasks/:enroll', '/my/surveys/:enroll'].map(path => ({ path, protectedPage: true, manager: path.startsWith('/manager/') })),
  // 기업 연수원 과정 상세는 공개, 연수원 내 강의실은 학습자·담당자 로그인 화면이다.
  ...['/cyber/:slug/courses/:openingId', '/cyber/:slug/my'].map(path => ({ path, protectedPage: path.endsWith('/my'), manager: false })),
]
const registered = new Set(VISIT_PAGES.map(page => page.path))
export function visitPath(route) {
  const path = route.matched?.at(-1)?.path
  return registered.has(path) ? path : null
}

// URL 전체·query·토큰을 복제하지 않고 서버가 다시 검증할 네 가지 업무 문맥만 허용한다.
export function checkedVisitContext(input) {
  if (input == null) return null
  const result = {}
  for (const field of Object.keys(input)) {
    if (!['siteSlug', 'contentKey', 'enrollmentId', 'itemId'].includes(field)) throw new Error('INVALID_VISIT_CONTEXT')
    const value = input[field]
    if (value == null) continue
    if (typeof value !== 'string') throw new Error('INVALID_VISIT_CONTEXT')
    const valid = field === 'siteSlug' ? /^[a-z0-9][a-z0-9-]{0,99}$/.test(value)
      : field === 'contentKey' ? /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(value)
        : /^[1-9][0-9]{0,18}$/.test(value) && BigInt(value) <= 9223372036854775807n
    if (!valid) throw new Error('INVALID_VISIT_CONTEXT')
    result[field] = value
  }
  return Object.keys(result).length ? result : null
}
export function waitsForVisitContext(route) {
  const path = visitPath(route)
  return ['/cyber/:slug', '/cyber/:slug/courses/:openingId', '/cyber/:slug/my', '/course/detail/:contentKey', '/my/learning/:id'].includes(path)
    || (CONTENT_PATHS.includes(path) && route.query?.slug != null)
}
export function automaticVisitContext(route) {
  const path = visitPath(route)
  return ['/my/assessments/:enroll/:assignment', '/my/tasks/:enroll', '/my/surveys/:enroll'].includes(path)
    ? checkedVisitContext({ enrollmentId: route.params?.enroll }) : null
}

export function visitNavigationKey(route) {
  const path = visitPath(route)
  const scoped = CONTENT_PATHS.includes(path) || path === '/course/detail/:contentKey'
  return JSON.stringify([route.path, scoped && typeof route.query?.slug === 'string' ? route.query.slug : null])
}
/** 승인된 화면 문맥이 세션 복원 뒤 한 번만 수집되게 하고, 지연 응답이 다른 화면에 섞이는 것을 차단한다. */
export function createVisitNavigationCollector({ collect, currentRoute, ready }) {
  let navigation = 0
  const seen = new Set()
  async function record(route, input = null, view = 'PAGE') {
    let context
    try { context = checkedVisitContext(input) } catch { return false }
    const expected = visitNavigationKey(route), currentNavigation = navigation
    await ready
    if (expected !== visitNavigationKey(currentRoute()) || currentNavigation !== navigation) return false
    const key = JSON.stringify([view, context])
    if (seen.has(key)) return false
    seen.add(key)
    return collect(route, context)
  }
  function automatic(route) { if (!waitsForVisitContext(route)) { try { void record(route, automaticVisitContext(route)) } catch { /* 미확인 문맥은 수집하지 않는다. */ } } }
  function navigate(to, from, failure, mounted) {
    if (failure || visitNavigationKey(to) === visitNavigationKey(from)) return
    navigation++; seen.clear()
    if (mounted) automatic(to)
  }
  return { record, automatic, navigate }
}

export function createVisitCollector({ client, isLoggedIn, uuid = () => crypto.randomUUID(), lock = fn => navigator.locks.request('techedu-visit-cookie', fn) }) {
  let queue = Promise.resolve()
  return (route, context = null) => {
    const path = visitPath(route)
    if (!path) return Promise.resolve(false)
    let checked
    try { checked = checkedVisitContext(context) } catch { return Promise.resolve(false) }
    const eventKey = uuid()
    // 최초 비회원 쿠키 발급을 탭 사이에서도 직렬화한다. 실패는 화면 이동을 막지 않고 자동 재발송하지 않는다.
    queue = queue.catch(() => false).then(() => lock(async () => {
      try {
        const body = { path, eventKey, ...(checked ? { context: checked } : {}) }
        const result = isLoggedIn()
          ? await client.request('/api/common/visits', { method: 'POST', body })
          : await client.publicRequest('/api/common/visits', body)
        return result.collected === true
      } catch { return false }
    })).catch(() => false)
    return queue
  }
}
